// @flow

import React from 'react';
import styled from 'styled-components';
import { colors, fonts } from 'shared/styleguide';
import { i18n } from 'shared/utils';
import { getCurrencySymbol } from 'shared/utils/currency';
import Panel from 'shared/components/common/Panel';

type ChildItem = {
  node: {
    id: string,
    itemNumber: string,
    name: string,
    cost: ?number,
    totalCalculatedCost: ?number,
  },
  usageQty: ?number,
};

type Props = {
  itemName: string,
  machineCost: number,
  materialCost: number,
  totalCost: number,
  childItems?: {
    edges: Array<ChildItem>,
  },
};

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Row = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 24px;

  @media (max-width: 1000px) {
    flex-direction: column;
  }
`;

const Column = styled.div`
  flex: 1;
`;

const CostSummaryCard = styled.div`
  border: 1px solid ${colors.paleGrey};
  border-radius: 8px;
  padding: 20px;
  background-color: ${colors.paleGreyThree};
`;

const TotalCostWrapper = styled.div`
  text-align: center;
  padding: 24px;
  background: linear-gradient(135deg, ${colors.azul} 0%, ${colors.sapphire} 100%);
  border-radius: 12px;
  color: ${colors.white};
  margin-bottom: 24px;
`;

const TotalCostLabel = styled.div`
  ${fonts.bodyRegular};
  opacity: 0.9;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const TotalCostAmount = styled.div`
  font-size: 48px;
  font-weight: 700;
  line-height: 1.2;
`;

const CostBreakdownItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid ${colors.paleGrey};
  
  &:last-child {
    border-bottom: none;
  }
`;

const CostLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CostDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${p => p.color};
`;

const CostName = styled.div`
  ${fonts.bodyBold};
  color: ${colors.charcoalGrey};
`;

const CostAmount = styled.div`
  ${fonts.bodyBold};
  color: ${colors.charcoalGrey};
`;

const CostPercentage = styled.div`
  ${fonts.smallRegular};
  color: ${colors.battleshipGrey};
  margin-top: 4px;
`;

const ComponentsTable = styled.div`
  border: 1px solid ${colors.paleGrey};
  border-radius: 8px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  padding: 16px;
  background-color: ${colors.paleGreyTwo};
  border-bottom: 2px solid ${colors.paleGrey};
  gap: 16px;
`;

const TableHeaderCell = styled.div`
  ${fonts.smallBold};
  color: ${colors.battleshipGrey};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  padding: 16px;
  border-bottom: 1px solid ${colors.paleGrey};
  gap: 16px;
  
  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${colors.veryLightBlue};
  }
`;

const TableCell = styled.div`
  ${fonts.bodyRegular};
  color: ${colors.charcoalGrey};
  display: flex;
  align-items: center;
`;

const SectionTitle = styled.h3`
  ${fonts.bodyBold};
  color: ${colors.charcoalGrey};
  margin-bottom: 16px;
  margin-top: 0;
`;

const ChartWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px;
  min-height: 300px;
`;

const PieChart = styled.svg`
  width: 280px;
  height: 280px;
`;

const LegendWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-left: 32px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendDot = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${p => p.color};
`;

const LegendText = styled.div`
  ${fonts.bodyRegular};
  color: ${colors.charcoalGrey};
`;

const LegendValue = styled.div`
  ${fonts.bodyBold};
  color: ${colors.charcoalGrey};
  margin-left: auto;
`;

const costColors = {
  material: colors.azul,
  labor: colors.turquoise,
};

// Simple pie chart for two values (material and machine)
const createSimplePieChart = (materialCost: number, machineCost: number) => {
  const total = materialCost + machineCost;
  if (total === 0) return null;
  
  const materialPercent = materialCost / total;
  const machinePercent = machineCost / total;

  const getCoordinatesForPercent = (percent: number) => {
    const x = Math.cos(2 * Math.PI * percent);
    const y = Math.sin(2 * Math.PI * percent);
    return [x, y];
  };

  const [startX, startY] = getCoordinatesForPercent(0);
  const [endX, endY] = getCoordinatesForPercent(materialPercent);
  const largeArcFlag = materialPercent > 0.5 ? 1 : 0;

  return (
    <>
      {/* Material slice */}
      <path
        d={[
          `M ${startX} ${startY}`,
          `A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY}`,
          'L 0 0',
        ].join(' ')}
        fill={costColors.material}
        stroke={colors.white}
        strokeWidth="0.02"
      />
      {/* Machine slice */}
      <path
        d={[
          `M ${endX} ${endY}`,
          `A 1 1 0 ${machinePercent > 0.5 ? 1 : 0} 1 ${startX} ${startY}`,
          'L 0 0',
        ].join(' ')}
        fill={costColors.labor}
        stroke={colors.white}
        strokeWidth="0.02"
      />
    </>
  );
};

const BOMCostBreakdown = ({ itemName, machineCost, materialCost, totalCost, childItems }: Props) => {
  // Use real data from props, fallback to 0 if not provided
  const total = totalCost || (machineCost + materialCost);
  const machinePercent = total > 0 ? (machineCost / total) * 100 : 0;
  const materialPercent = total > 0 ? (materialCost / total) * 100 : 0;

  // Process child items for the table
  const components = childItems?.edges?.map(edge => {
    const item = edge.node;
    const qty = edge.usageQty || 0;
    const unitCost = item.totalCalculatedCost || item.cost || 0;
    const totalCost = unitCost * qty;
    
    return {
      id: item.id,
      itemNumber: item.itemNumber,
      name: item.name,
      quantity: qty,
      unitCost,
      totalCost,
    };
  }) || [];

  return (
    <Panel 
      title={i18n.t('BOM Cost Analysis')}
      showHeaderBorder
    >
      <ContentWrapper>
        {/* Total Cost Display */}
        <TotalCostWrapper>
          <TotalCostLabel>{i18n.t('Total Cost')}</TotalCostLabel>
          <TotalCostAmount>{getCurrencySymbol()}{total.toFixed(2)}</TotalCostAmount>
        </TotalCostWrapper>

        <Row>
          {/* Cost Breakdown */}
          <Column>
            <CostSummaryCard>
              <CostBreakdownItem>
                <CostLabel>
                  <CostDot color={costColors.material} />
                  <div>
                    <CostName>{i18n.t('Material Cost')}</CostName>
                    <CostPercentage>
                      {materialPercent.toFixed(1)}% of total
                    </CostPercentage>
                  </div>
                </CostLabel>
                <CostAmount>{getCurrencySymbol()}{materialCost.toFixed(2)}</CostAmount>
              </CostBreakdownItem>

              <CostBreakdownItem>
                <CostLabel>
                  <CostDot color={costColors.labor} />
                  <div>
                    <CostName>{i18n.t('Machine Cost')}</CostName>
                    <CostPercentage>
                      {machinePercent.toFixed(1)}% of total
                    </CostPercentage>
                  </div>
                </CostLabel>
                <CostAmount>{getCurrencySymbol()}{machineCost.toFixed(2)}</CostAmount>
              </CostBreakdownItem>
            </CostSummaryCard>
          </Column>

          {/* Pie Chart */}
          <Column>
            <ChartWrapper>
              <PieChart viewBox="-1 -1 2 2" style={{ transform: 'rotate(-90deg)' }}>
                {createSimplePieChart(materialCost, machineCost)}
              </PieChart>
              <LegendWrapper>
                <LegendItem>
                  <LegendDot color={costColors.material} />
                  <LegendText>{i18n.t('Material')}</LegendText>
                  <LegendValue>
                    {materialPercent.toFixed(0)}%
                  </LegendValue>
                </LegendItem>
                <LegendItem>
                  <LegendDot color={costColors.labor} />
                  <LegendText>{i18n.t('Machine')}</LegendText>
                  <LegendValue>
                    {machinePercent.toFixed(0)}%
                  </LegendValue>
                </LegendItem>
              </LegendWrapper>
            </ChartWrapper>
          </Column>
        </Row>

        {/* Component Breakdown Table */}
        {components.length > 0 && (
          <div>
            <SectionTitle>{i18n.t('Component Breakdown')}</SectionTitle>
            <ComponentsTable>
              <TableHeader>
                <TableHeaderCell>{i18n.t('Component')}</TableHeaderCell>
                <TableHeaderCell>{i18n.t('Quantity')}</TableHeaderCell>
                <TableHeaderCell>{i18n.t('Unit Cost')}</TableHeaderCell>
                <TableHeaderCell>{i18n.t('Total Cost')}</TableHeaderCell>
              </TableHeader>
              {components.map(component => (
                <TableRow key={component.id}>
                  <TableCell>
                    <div>
                      <div>{component.name}</div>
                      <div style={{ fontSize: '12px', color: colors.battleshipGrey }}>
                        {component.itemNumber}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{component.quantity.toFixed(2)}</TableCell>
                  <TableCell>{getCurrencySymbol()}{component.unitCost.toFixed(2)}</TableCell>
                  <TableCell>{getCurrencySymbol()}{component.totalCost.toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </ComponentsTable>
          </div>
        )}
      </ContentWrapper>
    </Panel>
  );
};

export default BOMCostBreakdown;
